import React, {FC, useCallback, useEffect, useState} from 'react'
import {
  IReservationSource,
  initialReservationSource,
  reservationSourceValidation,
  RESERVATION_TABS as reservationTabs,
  BALANCING_TYPE,
  BALANCING_TX,
  TARIFFS,
  GO_GO_NOT,
  SHOW_HIDE,
  RISK,
  NAME_DISPLAY,
  IMPORTANCE,
  EXTENSION,
  PROVISION,
  KM,
  COST_REL,
  BALANGE_PERCENTAGE,
  RESERVATION_TYPES,
} from './ReservationSourceModel'

import {
  createReservationSource,
  deleteReservationSource,
  allReservationSource,
  updateReservationSource,
} from './Api'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import {ReservationSourceTable} from './ReservationSourceTable'
import {
  branches,
  currencies,
  fetchAsyncCustomerData,
} from '../../../../shared/models/SharedApiLookups'
import {AsyncCustomerModel} from '../../../../shared/models/ApiModels'
import AsyncSearchInput from '../../../../shared/components/AsyncSearchInput'
import {ACTIVE_OR_PASSIVE, YES_OR_NO} from '../../../../shared/models/SharedConstantLookups'

export const ReservationSourcePage: FC = () => {
  const [tab, setTab] = useState<string | 'form'>('form')
  const [reservationSources, setReservationSources] = useState<IReservationSource[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize, setPageSize] = useState(10)
  const [currencyOptions, setCurrencyOptions] = useState<{value: string; label: string}[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const fetchReservationSources = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      try {
        const res = await allReservationSource(page, pageSize, sort, order)
        const data = res.data.payload || res.data
        setReservationSources(data.content || data || [])
        setTotalPages(data.totalPages || 1)
      } catch (err) {
        const error = err as any
        if (error.response?.status === 401) {
          window.location.href = '/login'
        }
        console.error(error)
        showErrorToast('Yüklenirken bir hata oluştu')
      }
    },
    []
  )

  useEffect(() => {
    Promise.all([branches(), currencies()]).then(([brn, cr]) => {
      setBranchOptions(brn)
      setCurrencyOptions(cr)
    })
  }, [])

  useEffect(() => {
    fetchReservationSources(currentPage)
  }, [fetchReservationSources, currentPage])

  const handleSave = async (values: IReservationSource, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateReservationSource({...values, id: selectedId})
        showSuccessToast('Lokasyon başarıyla güncellendi')
      } else {
        await createReservationSource(values)
        showSuccessToast('Lokasyon başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchReservationSources(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }
  const handleEdit = (loc: IReservationSource, formik: FormikProps<IReservationSource>) => {
    formik.setValues(loc)
    setSelectedId(loc.id)
    setTab('form')
  }

  const handleDeleteClick = useCallback((ReservationSource: IReservationSource) => {
    setSelectedId(ReservationSource.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      try {
        await deleteReservationSource(selectedId)
        showSuccessToast('Başarıyla silindi')
        await fetchReservationSources(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Silinirken bir hata oluştu')
      } finally {
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchReservationSources(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchReservationSources]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchReservationSources(currentPage, column, order)
    },
    [currentPage, fetchReservationSources]
  )

  return (
    <>
      <Formik
        initialValues={initialReservationSource}
        validationSchema={reservationSourceValidation}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='reservationSourceForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={reservationTabs}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='reservationSourceForm' className='form card-body'>
                    <div className='row mb-6'>
                      <TextInput
                        name='reservationSourceName'
                        label='Rezervasyon Kaynağı'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='balancingType'
                        label='Bakiyelendirme'
                        options={BALANCING_TYPE}
                        wrapperClassName='col-md-3'
                      />
                      <SelectInput
                        name='balancingTransaction'
                        label='Bakiye'
                        options={BALANCING_TX}
                        wrapperClassName='col-md-3'
                      />
                    </div>

                    <div className='row mb-6'>
                      <AsyncSearchInput<AsyncCustomerModel>
                        name='company'
                        label='Firma Adı (Cari)'
                        placeholder='Firma adı girin...'
                        fetchHandler={fetchAsyncCustomerData}
                        displayField={(item) => `${item.firstName} ${item.lastName}`}
                        onSelect={(item) => {
                          formik.setFieldValue('companyId', item.id) 
                        }}
                        onClear={() => console.log('Cari temizlendi')}
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='balancePercentage'
                        label='Bakiye'
                        wrapperClassName='col-md-3'
                        options={BALANGE_PERCENTAGE}
                      />
                      <TextInput
                        name='balancePercentageRate'
                        label='Oran'
                        wrapperClassName='col-md-3'
                      />
                    </div>

                    <div className='row mb-6'>
                      <SelectInput
                        name='currency'
                        label='Para Birimi'
                        wrapperClassName='col-md-6'
                        options={currencyOptions}
                      />
                      <SelectInput
                        name='tariffType'
                        label='Tarife'
                        options={TARIFFS}
                        wrapperClassName='col-md-3'
                      />
                      <SelectInput
                        name='customerPaymentFlag'
                        label='Sadece Müşteri Ödemeli'
                        options={YES_OR_NO}
                        wrapperClassName='col-md-3'
                      />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='workingOffice'
                        label='Çalışma Ofisi'
                        wrapperClassName='col-md-6'
                        options={branchOptions}
                      />
                      <SelectInput
                        name='status'
                        label='Durum'
                        options={ACTIVE_OR_PASSIVE}
                        wrapperClassName='col-md-3'
                      />
                      <SelectInput
                        name='fullCreditLocation'
                        label='Full Credite İzin Ver'
                        options={YES_OR_NO}
                        wrapperClassName='col-md-3'
                      />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='type'
                        label='Türü'
                        wrapperClassName='col-md-6'
                        options={RESERVATION_TYPES}
                      />
                      <TextInput
                        name='email'
                        type='email'
                        label='Mail Adresi'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='maxDays'
                        type='number'
                        label='Max Gün'
                        wrapperClassName='col-md-3'
                      />
                      <TextInput
                        name='additionalDriver'
                        type='number'
                        label='Ek Sürücü'
                        wrapperClassName='col-md-3'
                      />
                      <TextInput
                        name='wifi'
                        type='number'
                        label='Wifi'
                        wrapperClassName='col-md-3'
                      />
                       <TextInput
                        name='babyseat'
                        type='number'
                        label='Bebek Koltuğu'
                        wrapperClassName='col-md-3'
                      />
                    </div>

                    <div className='row mb-6'> 
                      <TextInput
                        name='navigation'
                        type='number'
                        label='Navigasyon'
                        wrapperClassName='col-md-3'
                      />
                      <TextInput
                        name='mandatoryServiceFee'
                        type='number'
                        label='Zorunlu Hizmet Bedeli'
                        wrapperClassName='col-md-3'
                      />
                      <TextInput
                        name='balanceAmount'
                        type='number'
                        label='Bakiye Tutarı'
                        wrapperClassName='col-md-3'
                      />
                    </div>
                  </Form>
                </div>
              )}

              {tab === 'details' && (
                <div className='card mb-5'>
                  <div className='card-body'>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px'}}>
                      <div>
                        <div className='p-4 rounded-2'>
                          {[
                            {
                              key: 'kdvExemptionAvailable',
                              label: 'Kdv Muafiyeti Var (Bilmiyorsanız İşaretlemeyin)',
                            },
                            {key: 'xmlServiceSpecial', label: 'XML Hizmet Özel'},
                            {
                              key: 'xmlCompanyMailNotification',
                              label: 'XML Firmasının Müşterisine Mail GİTMESİN',
                            },
                            {key: 'scdwIncluded', label: 'SCDW Dahil'},
                            {key: 'cdwIncluded', label: 'CDW Dahil'},
                            {key: 'lcfIncluded', label: 'LCF Dahil'},
                            {key: 'paiIncluded', label: 'PAI Dahil'},
                          ].map(({key, label}) => (
                            <div className='form-check mb-3' key={key}>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                id={key}
                                name={key}
                                checked={(formik.values as any)[key]}
                                onChange={formik.handleChange}
                              />
                              <label className='form-check-label' htmlFor={key}>
                                {label}
                              </label>
                            </div>
                          ))}

                          <div className='mt-4'>
                            <label className='form-label fw-semibold'>
                              Otomatik No Show Süresi (Saat)
                            </label>
                            <input
                              type='number'
                              name='automaticNoShowDuration'
                              value={(formik.values as any).automaticNoShowDuration ?? ''}
                              onChange={formik.handleChange}
                              className='form-control bg-white'
                            />
                          </div>

                          <div className='mt-3'>
                            <TextInput
                              name='futureInvoiceRate'
                              type='number'
                              label='Gelecek Fatura Oranı'
                              wrapperClassName='w-100'
                            />
                            <TextInput
                              name='xmlPriceMultiplier'
                              type='number'
                              label='XML Fiyat Katsayı'
                              wrapperClassName='w-100'
                            />
                            <TextInput
                              name='xmlServiceMultiplier'
                              type='number'
                              label='XML Hizmet Katsayı'
                              wrapperClassName='w-100'
                            />
                            <TextInput
                              name='xmlAdvancePaymentRate'
                              type='number'
                              label='XML Ön Ödeme Oranı'
                              wrapperClassName='w-100'
                            />
                            <TextInput
                              name='frameCode'
                              label='Frame Kod'
                              wrapperClassName='w-100'
                            />

                            <SelectInput
                              name='agencyPriceChange'
                              label='Acente Fiyat Değiştir'
                              options={YES_OR_NO}
                              wrapperClassName='w-100'
                            />
                            <SelectInput
                              name='provisionType'
                              label='Provizyon'
                              options={PROVISION}
                              wrapperClassName='w-100'
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '16px'}}>
                          <TextInput
                            name='faz1Timeout'
                            type='number'
                            label='Faz1 Timeout'
                            wrapperClassName='w-100'
                          />
                          <TextInput
                            name='faz2Timeout'
                            type='number'
                            label='Faz2 Timeout'
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='automaticMailSms'
                            label='Otomatik Mail-SMS'
                            options={GO_GO_NOT}
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='xmlPaymentType'
                            label='XML Payment Türü'
                            options={[
                              {label: 'Ödeme', value: 'Ödeme'},
                              {label: 'Ön Ödeme', value: 'Ön Ödeme'},
                            ]}
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='secretOption'
                            label='Gizle'
                            options={SHOW_HIDE}
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='riskAnalysis'
                            label='Risk Analizi'
                            options={RISK}
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='nameDisplay'
                            label='İsim göster'
                            options={NAME_DISPLAY}
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='balanceNonRentConversion'
                            label='Bakiye Önemsiz Kira Dönüştür'
                            options={IMPORTANCE}
                            wrapperClassName='w-100'
                          />

                          <SelectInput
                            name='extensionProcess'
                            label='Uzatma İşlemi'
                            options={EXTENSION}
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='unlimitedKm'
                            label='Sınırsız KM'
                            options={KM}
                            wrapperClassName='w-100'
                          />
                          <SelectInput
                            name='coefficientCostRelationship'
                            label='Katsayıyı Maliyet İlişkisi'
                            options={COST_REL}
                            wrapperClassName='w-100'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === 'media' && (
                <div className='card mb-5'>
                  <div className='card-body'>
                    <h5 className='mb-4'>Fotoğraf Ekle (logo)</h5>
                    <input type='file' className='form-control mb-4' />
                    <TextAreaInput name='secretOption' label='Not / Açıklama' />
                  </div>
                </div>
              )}

              {tab === 'prices' && (
                <div className='card'>
                  <div className='card-body'>
                    <em>Fiyatlar sekmesi backend netleşince doldurulacak.</em>
                  </div>
                </div>
              )}

              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body'>
                    <ReservationSourceTable
                      data={reservationSources}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(row) => handleEdit(row, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu rezervasyon kaynağını silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}
